import React, { useEffect, useState } from "react";
import BaseModalAnimated from "../../components/modal/BaseModalAnimated";
import CustomButton from "../../components/form/CustomButton";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
    getPrefixes,
    clientsResource,
} from "../../helpers/configRoutes/backend_helper";
import CustomInput from "../../components/form/CustomInput";
import { showFormErrors } from "../../helpers/showFormErrors";
import CustomSelect from "../../components/form/CustomSelect";
import { validatePhones } from "../../helpers/forms/Validation";

const AddUpdClientModal = ({
    modal,
    setModal,
    clientItem,
    setFilters,
    filters,
}) => {
    const [prefixes, setPrefixes] = useState([]);
    const [loading, setLoading] = useState(false);

    const baseitem = {
        id: null,
        company_name: "",
        contact_name: "",
        email: "",
        email_billing: "",
        address: "",
        net_payment: "",
        phone: "",
        prefix_id: "",
        company_id: "",
    };

    const validation = useFormik({
        initialValues: baseitem,
        enableReinitialize: false,
        validationSchema: Yup.object({
            email: Yup.string()
                .email("Invalid email")
                .required("Email is required"),
            email_billing: Yup.string()
                .email("Invalid email")
                .required("Email is required"),
            company_name: Yup.string().required("Name is required"),
            prefix_id: Yup.string().required("Prefix is required"),
            phone: validatePhones(true),
            contact_name: Yup.string().required("Contact name is required"),
            address: Yup.string().required("Address is required"),
            net_payment: Yup.string().required("Net payment is required"),
        }),
        onSubmit: async (values, { setErrors }) => {
            setLoading(true);

            const response = await clientsResource(
                values.id ? "put" : "post",
                values,
                values.id
            );

            if (response.status) {
                // refresca lista
                setFilters({ ...filters, search: filters.search });
                setModal({ ...modal, show: false });
            } else {
                setErrors(response.errors || {});
            }

            setLoading(false);
        },
    });

    // Actualizar valores cuando se abre el modal
    useEffect(() => {
        if (modal.show) {
            if (clientItem && clientItem.id) {
                validation.setValues(
                    {
                        ...baseitem,
                        ...clientItem,
                    },
                    false
                );
            } else {
                validation.resetForm({ values: baseitem });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modal.show]);

    // Cargar prefijos
    useEffect(() => {
        const getPrefixesCatalog = async () => {
            const response = await getPrefixes();
            if (response.status) {
                setPrefixes(response.prefixes || []);
            }
        };
        getPrefixesCatalog();
    }, []);

    // Componente visual para separar secciones
    const SectionDivider = ({ label }) => (
        <div className="flex items-center gap-3 my-5">
            <div className="h-px bg-gray-200 flex-1"></div>
            <span className="text-[10px] uppercase font-bold text-gray-400 tracking-widest">{label}</span>
            <div className="h-px bg-gray-200 flex-1"></div>
        </div>
    );

    return (
        <BaseModalAnimated
            modal={modal}
            setModal={setModal}
            size={"max-w-4xl"} // Hice el modal un poco más ancho para que respire
            title={
                validation.values.id ? "Edit Client Information" : "Add New Client"
            }
        >
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    validation.handleSubmit();
                    showFormErrors(validation);
                    return false;
                }}
                className="flex flex-col h-full"
            >
                {/* Body Scrollable */}
                <div className="p-6 overflow-y-auto custom-scrollbar bg-gray-50/50">

                    {/* --- SECCIÓN 1: IDENTIDAD --- */}
                    <div className="grid grid-cols-2 gap-4">
                        <CustomInput
                            label="Company Name"
                            type="text"
                            name="company_name"
                            placeholder="e.g. Logistics Solutions LLC"
                            labelClassName="text-xs font-semibold text-gray-600 mb-1"
                            className="bg-white"
                            error={validation.errors.company_name}
                            value={validation.values.company_name}
                            handleChange={validation.handleChange}
                        />
                        <CustomInput
                            label="Physical Address"
                            type="text"
                            name="address"
                            placeholder="Street, City, State, Zip"
                            labelClassName="text-xs font-semibold text-gray-600 mb-1"
                            className="bg-white"
                            error={validation.errors.address}
                            value={validation.values.address}
                            handleChange={validation.handleChange}
                        />
                    </div>

                    <SectionDivider label="Operations & Contact" />

                    {/* --- SECCIÓN 2: CONTACTO --- */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div className="grid grid-cols-2 md:grid-cols-2 gap-5">
                            <CustomInput
                                label="Contact Name"
                                type="text"
                                name="contact_name"
                                labelClassName="text-xs font-semibold text-gray-600 mb-1"
                                error={validation.errors.contact_name}
                                value={validation.values.contact_name}
                                handleChange={validation.handleChange}
                            />
                            <CustomInput
                                label="Operations Email"
                                type="text"
                                name="email"
                                placeholder="ops@client.com"
                                labelClassName="text-xs font-semibold text-gray-600 mb-1"
                                error={validation.errors.email}
                                value={validation.values.email}
                                handleChange={validation.handleChange}
                            />
                        </div>
                        {/* Bloque de Teléfono Unificado */}
                        <div>
                            <label className="text-xs font-semibold text-gray-600 m block">Phone Number</label>
                            <div className="flex gap-2">
                                
                                    <CustomSelect
                                        options={prefixes}
                                        name="prefix_id"
                                        placeholder="+1"
                                        label={true}
                                        className="text-sm"
                                        labelClassName="text-xs font-semibold text-gray-600 mb-1"
                                        error={validation.errors.prefix_id}
                                        value={validation.values.prefix_id}
                                        handleChange={validation.handleChange}
                                    />

                                
                                    <CustomInput
                                        type="text"
                                        name="phone"
                                        className="text-sm"
                                        error={validation.errors.phone}
                                        value={validation.values.phone}
                                        handleChange={(e) => {
                                            const sanitized = e.target.value.replace(/\D/g, "");
                                            validation.setFieldValue("phone", sanitized);
                                        }}
                                    />
                                
                            </div>
                        </div>


                    </div>

                    <SectionDivider label="Financial & Billing" />

                    {/* --- SECCIÓN 3: FACTURACIÓN --- */}
                    <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <CustomInput
                                label="Billing Email"
                                type="text"
                                name="email_billing"
                                placeholder="invoices@client.com"
                                labelClassName="text-xs font-bold text-gray-700 mb-1"
                                error={validation.errors.email_billing}
                                value={validation.values.email_billing}
                                handleChange={validation.handleChange}
                            />

                            <div className="relative">
                                <CustomInput
                                    label="Net Payment Terms"
                                    type="number"
                                    name="net_payment"
                                    placeholder="e.g. 30"
                                    labelClassName="text-xs font-bold text-gray-700 mb-1"
                                    error={validation.errors.net_payment}
                                    value={validation.values.net_payment}
                                    handleChange={validation.handleChange}
                                />
                                <p className="text-[10px] text-gray-400 mt-1 italic text-right">
                                    Payment days after POD
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 px-6 py-4 bg-white border-t border-gray-100">
                    <button
                        type="button"
                        onClick={() => setModal({ ...modal, show: false })}
                        className="
                            px-4 py-2 rounded-xl
                            text-xs font-medium
                            text-gray-500
                            hover:bg-gray-50
                            border border-transparent hover:border-gray-200
                            transition-all
                        "
                    >
                        Cancel
                    </button>

                    <CustomButton
                        loading={loading}
                        textLoad="Saving..."
                        type="submit"
                        className="
                            px-6 py-2 rounded-xl
                            bg-cs-primary hover:bg-cs-primary-hover
                            text-white text-xs font-bold
                            shadow-md hover:shadow-lg
                            transition-all
                        "
                    >
                        {validation.values.id ? "Update Client" : "Create Client"}
                    </CustomButton>
                </div>
            </form>
        </BaseModalAnimated>
    );
};

export default AddUpdClientModal;